/**
 * Custom hooks for working with filters.
 *
 * Provides convenient access to filter state and loading filter configurations.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useFilterStore, FilterValue, LocalFilterConfig } from '../stores/filterStore'
import { useStudyStore } from '../stores/studyStore'
import {
  getStudyFilterConfig,
  getFilterValues,
  getPageFilters,
  getLocalFilterValues,
  listSavedFilters,
  getSavedFilter,
  createSavedFilter,
  updateSavedFilter,
  deleteSavedFilter,
  getDefaultSavedFilter,
  CreateSavedFilterRequest,
  UpdateSavedFilterRequest,
  PageFilterConfig,
} from '../lib/filterApi'
import { fetchStudyPages, StudyPage } from '../lib/studyConfigApi'

// =============================================================================
// Filter Configuration
// =============================================================================

/**
 * Load and manage study filter configuration.
 */
export function useStudyFilterConfig(studyId: string | null) {
  const setFilterConfig = useFilterStore((state) => state.setFilterConfig)
  const setLoadingConfig = useFilterStore((state) => state.setLoadingConfig)

  const query = useQuery({
    queryKey: ['filter-config', studyId],
    queryFn: () => (studyId ? getStudyFilterConfig(studyId) : null),
    enabled: !!studyId,
    staleTime: Infinity, // Filter config doesn't change often
  })

  // Update store when config loads
  useEffect(() => {
    setLoadingConfig(query.isLoading)
    if (query.data !== undefined) {
      setFilterConfig(query.data)
    }
  }, [query.data, query.isLoading, setFilterConfig, setLoadingConfig])

  return query
}

/**
 * Load filter values with cascading support.
 */
export function useFilterValues(studyId: string | null) {
  const globalFilters = useFilterStore((state) => state.globalFilters)
  const setCachedGlobalValues = useFilterStore((state) => state.setCachedGlobalValues)
  const setLoadingValues = useFilterStore((state) => state.setLoadingValues)

  // Convert globalFilters to the format expected by the API (exclude null values)
  const filterParams = Object.entries(globalFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      // Type guard to ensure value matches API expectation
      if (typeof value === 'string' || Array.isArray(value) ||
          (typeof value === 'object' && ('start' in value || 'end' in value))) {
        acc[key] = value as string | string[] | { start?: string; end?: string }
      }
    }
    return acc
  }, {} as Record<string, string | string[] | { start?: string; end?: string }>)

  const query = useQuery({
    queryKey: ['filter-values', studyId, filterParams],
    queryFn: () => (studyId ? getFilterValues(studyId, filterParams) : null),
    enabled: !!studyId,
    staleTime: 30000, // 30 seconds - may need to refresh for cascading
  })

  // Update store when values load
  useEffect(() => {
    setLoadingValues(query.isLoading)
    if (query.data?.values) {
      setCachedGlobalValues(query.data.values)
    }
  }, [query.data, query.isLoading, setCachedGlobalValues, setLoadingValues])

  return query
}

// =============================================================================
// Page Filter Config
// =============================================================================

/**
 * Helper to find page ID from page key using study pages.
 */
function findPageIdByKey(pages: StudyPage[], pageKey: string): number | null {
  for (const page of pages) {
    if (page.pageKey === pageKey) {
      return page.id
    }
    if (page.children?.length) {
      const childResult = findPageIdByKey(page.children, pageKey)
      if (childResult !== null) {
        return childResult
      }
    }
  }
  return null
}

/**
 * Convert API PageFilterConfig to store LocalFilterConfig.
 */
function convertToLocalFilterConfig(apiConfig: PageFilterConfig): LocalFilterConfig {
  return {
    id: apiConfig.id,
    filterKey: apiConfig.filterKey,
    label: apiConfig.label,
    filterType: apiConfig.filterType,
    sourceDataset: apiConfig.sourceDataset,
    sourceColumn: apiConfig.sourceColumn,
    sortOrder: apiConfig.sortOrder,
    isEnabled: apiConfig.isEnabled,
    defaultValues: apiConfig.defaultValues,
  }
}

/**
 * Load page filter configuration for a specific page.
 * Resolves pageKey to pageId and fetches filter configs.
 */
export function usePageFilterConfig(studyId: string | null, pageKey: string | null) {
  const setPageFilterConfig = useFilterStore((state) => state.setPageFilterConfig)
  const setCachedLocalValues = useFilterStore((state) => state.setCachedLocalValues)
  const pageFilterConfigs = useFilterStore((state) =>
    pageKey ? state.pageFilterConfigs[pageKey] : undefined
  )

  // First, fetch study pages to resolve pageKey to pageId
  const pagesQuery = useQuery({
    queryKey: ['study-pages', studyId],
    queryFn: () => (studyId ? fetchStudyPages(studyId) : []),
    enabled: !!studyId && !!pageKey,
    staleTime: Infinity, // Pages don't change often
  })

  // Find the pageId from pageKey
  const pageId = useMemo(() => {
    if (!pagesQuery.data || !pageKey) return null
    return findPageIdByKey(pagesQuery.data, pageKey)
  }, [pagesQuery.data, pageKey])

  // Fetch page filters once we have the pageId
  const filtersQuery = useQuery({
    queryKey: ['page-filters', studyId, pageId],
    queryFn: () => (studyId && pageId ? getPageFilters(studyId, pageId) : []),
    enabled: !!studyId && !!pageId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Fetch local filter values (distinct values for dropdowns)
  const valuesQuery = useQuery({
    queryKey: ['local-filter-values', studyId, pageId],
    queryFn: () => (studyId && pageId ? getLocalFilterValues(studyId, pageId) : { values: {} }),
    enabled: !!studyId && !!pageId && (filtersQuery.data?.length ?? 0) > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Update store when filters load
  useEffect(() => {
    if (filtersQuery.data && pageKey) {
      const localConfigs = filtersQuery.data.map(convertToLocalFilterConfig)
      setPageFilterConfig(pageKey, localConfigs)
    }
  }, [filtersQuery.data, pageKey, setPageFilterConfig])

  // Update store when filter values load
  useEffect(() => {
    if (valuesQuery.data?.values && pageKey) {
      setCachedLocalValues(pageKey, valuesQuery.data.values)
    }
  }, [valuesQuery.data, pageKey, setCachedLocalValues])

  return {
    isLoading: pagesQuery.isLoading || filtersQuery.isLoading || valuesQuery.isLoading,
    error: pagesQuery.error || filtersQuery.error || valuesQuery.error,
    configs: pageFilterConfigs || [],
  }
}

// =============================================================================
// Global Filters
// =============================================================================

/**
 * Hook for managing global filters.
 */
export function useGlobalFilterActions() {
  const setGlobalFilter = useFilterStore((state) => state.setGlobalFilter)
  const resetGlobalFilters = useFilterStore((state) => state.resetGlobalFilters)
  const globalFilters = useFilterStore((state) => state.globalFilters)
  const filterConfig = useFilterStore((state) => state.filterConfig)
  const cachedValues = useFilterStore((state) => state.cachedGlobalValues)

  const setFilter = useCallback(
    (key: string, value: FilterValue) => {
      setGlobalFilter(key, value)
    },
    [setGlobalFilter]
  )

  const clearFilter = useCallback(
    (key: string) => {
      setGlobalFilter(key, null)
    },
    [setGlobalFilter]
  )

  const resetAll = useCallback(() => {
    resetGlobalFilters()
  }, [resetGlobalFilters])

  return {
    filters: globalFilters,
    config: filterConfig?.globalFilters || [],
    cachedValues,
    setFilter,
    clearFilter,
    resetAll,
  }
}

// =============================================================================
// Local Filters
// =============================================================================

/**
 * Hook for managing local (page-specific) filters.
 */
export function useLocalFilterActions(pageKey: string) {
  const localFilters = useFilterStore((state) => state.localFilters[pageKey] || {})
  const setLocalFilter = useFilterStore((state) => state.setLocalFilter)
  const resetLocalFilters = useFilterStore((state) => state.resetLocalFilters)
  const pageFilterConfigs = useFilterStore((state) => state.pageFilterConfigs[pageKey] || [])

  const setFilter = useCallback(
    (filterKey: string, value: FilterValue) => {
      setLocalFilter(pageKey, filterKey, value)
    },
    [pageKey, setLocalFilter]
  )

  const clearFilter = useCallback(
    (filterKey: string) => {
      setLocalFilter(pageKey, filterKey, null)
    },
    [pageKey, setLocalFilter]
  )

  const resetAll = useCallback(() => {
    resetLocalFilters(pageKey)
  }, [pageKey, resetLocalFilters])

  return {
    filters: localFilters,
    config: pageFilterConfigs,
    setFilter,
    clearFilter,
    resetAll,
  }
}

// =============================================================================
// Module Filters
// =============================================================================

/**
 * Hook for managing module (column-level) filters.
 */
export function useModuleFilterActions(moduleId: string) {
  const moduleFilters = useFilterStore((state) => state.moduleFilters[moduleId] || {})
  const setModuleFilter = useFilterStore((state) => state.setModuleFilter)
  const resetModuleFilters = useFilterStore((state) => state.resetModuleFilters)

  const setFilter = useCallback(
    (column: string, value: FilterValue) => {
      setModuleFilter(moduleId, column, value)
    },
    [moduleId, setModuleFilter]
  )

  const clearFilter = useCallback(
    (column: string) => {
      setModuleFilter(moduleId, column, null)
    },
    [moduleId, setModuleFilter]
  )

  const resetAll = useCallback(() => {
    resetModuleFilters(moduleId)
  }, [moduleId, resetModuleFilters])

  return {
    filters: moduleFilters,
    setFilter,
    clearFilter,
    resetAll,
  }
}

// =============================================================================
// Saved Filters
// =============================================================================

/**
 * Hook for loading and managing saved filters.
 */
export function useSavedFilters(studyId: string | null, pageKey?: string) {
  const queryClient = useQueryClient()
  const setSavedFilters = useFilterStore((state) => state.setSavedFilters)
  const applySavedFilter = useFilterStore((state) => state.applySavedFilter)

  // Query for listing saved filters
  const listQuery = useQuery({
    queryKey: ['saved-filters', studyId, pageKey],
    queryFn: () => (studyId ? listSavedFilters(studyId, pageKey) : []),
    enabled: !!studyId,
  })

  // Update store when filters load
  useEffect(() => {
    if (listQuery.data) {
      // Convert brief to full format for store
      setSavedFilters(
        listQuery.data.map((f) => ({
          id: f.id,
          name: f.name,
          description: f.description,
          filterDefinition: { globalFilters: {}, localFilters: {} },
          pageKey: f.pageKey,
          isShared: f.isShared,
          isDefault: f.isDefault,
          createdBy: f.createdBy,
          createdAt: f.createdAt,
        }))
      )
    }
  }, [listQuery.data, setSavedFilters])

  // Mutation for creating saved filter
  const createMutation = useMutation({
    mutationFn: (data: CreateSavedFilterRequest) =>
      studyId ? createSavedFilter(studyId, data) : Promise.reject('No study'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-filters', studyId] })
    },
  })

  // Mutation for updating saved filter
  const updateMutation = useMutation({
    mutationFn: ({ filterId, data }: { filterId: string; data: UpdateSavedFilterRequest }) =>
      studyId ? updateSavedFilter(studyId, filterId, data) : Promise.reject('No study'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-filters', studyId] })
    },
  })

  // Mutation for deleting saved filter
  const deleteMutation = useMutation({
    mutationFn: (filterId: string) =>
      studyId ? deleteSavedFilter(studyId, filterId) : Promise.reject('No study'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-filters', studyId] })
    },
  })

  // Function to apply a saved filter
  const applyFilter = useCallback(
    async (filterId: string) => {
      if (!studyId) return
      const filter = await getSavedFilter(studyId, filterId)
      applySavedFilter(filter)
    },
    [studyId, applySavedFilter]
  )

  return {
    savedFilters: listQuery.data || [],
    isLoading: listQuery.isLoading,
    applyFilter,
    createFilter: createMutation.mutate,
    updateFilter: updateMutation.mutate,
    deleteFilter: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

// =============================================================================
// Combined Filter State
// =============================================================================

/**
 * Hook for getting combined filter state for API queries.
 * Returns filters in the format expected by the backend.
 */
export function useQueryFilters(pageKey: string, moduleId?: string) {
  const globalFilters = useFilterStore((state) => state.globalFilters)
  const localFilters = useFilterStore((state) => state.localFilters[pageKey] || {})
  const moduleFiltersFromStore = useFilterStore((state) =>
    moduleId ? state.moduleFilters[moduleId] || {} : {}
  )
  const moduleFilters = moduleId ? moduleFiltersFromStore : {}

  // Filter out null/empty values
  const activeGlobalFilters = Object.entries(globalFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '' &&
        !(Array.isArray(value) && value.length === 0)) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, FilterValue>)

  const activeLocalFilters = Object.entries(localFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '' &&
        !(Array.isArray(value) && value.length === 0)) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, FilterValue>)

  const activeModuleFilters = Object.entries(moduleFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '' &&
        !(Array.isArray(value) && value.length === 0)) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, FilterValue>)

  return {
    globalFilters: activeGlobalFilters,
    localFilters: activeLocalFilters,
    moduleFilters: activeModuleFilters,
    hasFilters: Object.keys(activeGlobalFilters).length > 0 ||
                Object.keys(activeLocalFilters).length > 0 ||
                Object.keys(activeModuleFilters).length > 0,
  }
}

/**
 * Hook to initialize filters when study changes.
 * Also auto-applies the user's default filter if one is set.
 */
export function useFilterInitialization() {
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const resetAllFilters = useFilterStore((state) => state.resetAllFilters)
  const applySavedFilter = useFilterStore((state) => state.applySavedFilter)
  const prevStudyIdRef = useRef<string | null | undefined>(undefined)

  // Load filter config
  useStudyFilterConfig(currentStudy?.studyId || null)

  // Load filter values
  useFilterValues(currentStudy?.studyId || null)

  // Reset filters and apply default when study changes
  useEffect(() => {
    const currentStudyId = currentStudy?.studyId
    const prevStudyId = prevStudyIdRef.current

    // Only reset if:
    // 1. We had a previous study (not initial load from undefined)
    // 2. The study actually changed to a different one
    if (prevStudyId !== undefined && prevStudyId !== currentStudyId) {
      resetAllFilters()

      // Auto-apply default filter for the new study
      if (currentStudyId) {
        const loadDefaultFilter = async () => {
          try {
            const defaultFilter = await getDefaultSavedFilter(currentStudyId)
            if (defaultFilter) {
              applySavedFilter({
                id: defaultFilter.id,
                name: defaultFilter.name,
                description: defaultFilter.description,
                filterDefinition: defaultFilter.filterDefinition,
                pageKey: defaultFilter.pageKey,
                isShared: defaultFilter.isShared,
                isDefault: defaultFilter.isDefault,
                createdBy: defaultFilter.createdBy,
                createdAt: defaultFilter.createdAt,
              })
            }
          } catch (error) {
            // Silently fail - default filters are optional
            console.error('Failed to load default filter:', error)
          }
        }

        loadDefaultFilter()
      }
    }

    // Update ref for next comparison
    prevStudyIdRef.current = currentStudyId
  }, [currentStudy?.studyId, resetAllFilters, applySavedFilter])

  return { studyId: currentStudy?.studyId }
}
